// webapp/src/components/feedback/FeedbackWizardSteps.tsx

import type { WizardState } from '../../services/feedbackService';

export interface WizardStepDef {
  /** Unique key matching WizardState field name */
  fieldKey: keyof WizardState;
  /** Question displayed as step heading */
  title: string;
  /** Textarea placeholder text */
  placeholder: string;
  /** If true, user can skip this step without entering text */
  isOptional?: boolean;
}

/**
 * Bug report steps (5 total: 4 questions + review).
 * Review step is handled separately by the wizard shell.
 */
export const BUG_STEPS: WizardStepDef[] = [
  {
    fieldKey: 'whatDoing',
    title: 'What were you doing?',
    placeholder: 'I was placing devices on the floor plan...',
  },
  {
    fieldKey: 'whatHappened',
    title: 'What happened?',
    placeholder: 'The device disappeared after I dropped it...',
  },
  {
    fieldKey: 'whatExpected',
    title: 'What should have happened?',
    placeholder: 'The device should have appeared on the floor plan where I dropped it...',
  },
  // Severity step is handled specially by the wizard shell (not a textarea)
];

/**
 * Feature request steps (4 total: 3 questions + review).
 */
export const FEATURE_STEPS: WizardStepDef[] = [
  {
    fieldKey: 'goal',
    title: 'What are you trying to accomplish?',
    placeholder: 'I need to export floor plans as DXF files for AutoCAD...',
  },
  {
    fieldKey: 'currentWorkaround',
    title: 'How do you currently do it?',
    placeholder: 'I take a screenshot and trace it in AutoCAD...',
    isOptional: true,
  },
  {
    fieldKey: 'desiredImprovement',
    title: 'What would make it better?',
    placeholder: 'A DXF export button on the canvas tab...',
  },
];

/**
 * Smart placeholder: returns a context-aware placeholder for "What were you doing?"
 * based on current survey context.
 */
export function getSmartPlaceholder(
  step: WizardStepDef,
  surveyContext?: { floorName?: string; activeTab?: string },
): string {
  if (step.fieldKey !== 'whatDoing' || !surveyContext) {
    return step.placeholder;
  }

  const { floorName, activeTab } = surveyContext;

  if (activeTab === 'Canvas' && floorName) {
    return `I was placing devices on ${floorName}...`;
  }
  if (activeTab === 'Photos') {
    return 'I was managing site photos...';
  }
  if (activeTab === 'BOM' || activeTab === 'Bill of Materials') {
    return 'I was reviewing the Bill of Materials...';
  }
  if (activeTab === 'Survey Data') {
    return 'I was filling out the survey data form...';
  }
  if (activeTab === 'Scope of Work') {
    return 'I was working on the Scope of Work...';
  }

  return step.placeholder;
}

/** Total steps for each flow (questions + severity/review) */
export function getTotalSteps(type: 'bug' | 'feature'): number {
  // Bug: 3 text steps + 1 severity + 1 review = 5
  // Feature: 3 text steps + 1 review = 4
  return type === 'bug' ? 5 : 4;
}
